import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useSelector } from 'react-redux';
import MainLayout from '../../../layout/main-layout';
import { APP_API_URL } from '../../../utils';
import formatNumber, { defaultFilterDates, formatDate } from '../../../utils/functions';

const PER_PAGE = 10;
const reasonLabel = (reason) => (reason === 'gate' ? 'Gâté' : 'Cassé');

export default function DamagedGoodsList() {
  const user = useSelector((state) => state.auth.user);
  const isRole13 = Number(user?.personnels?.[0]?.poste_id) === 13;
  const [allData, setAllData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [appliedFilters, setAppliedFilters] = useState(() => ({
    search: '',
    reason: '',
    ...defaultFilterDates(),
  }));
  const [draftFilters, setDraftFilters] = useState(() => defaultFilterDates());
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/damaged-goods/list`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          fromDate: appliedFilters.dateFrom || undefined,
          toDate: appliedFilters.dateTo || undefined,
          search: appliedFilters.search || undefined,
          reason: appliedFilters.reason || undefined,
        },
      });
      setAllData(response.data?.items || []);
    } catch (error) {
      console.error(error);
      setAllData([]);
    } finally {
      setIsLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedFilters((prev) => (
        prev.search === searchInput ? prev : { ...prev, search: searchInput }
      ));
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const stats = useMemo(() => ({
    total: allData.length,
    qty: allData.reduce((acc, item) => acc + (parseFloat(item.quantity) || 0), 0),
    cost: allData.reduce((acc, item) => acc + (parseFloat(item.total_cost_price) || 0), 0),
  }), [allData]);

  const totalPages = Math.max(1, Math.ceil(allData.length / PER_PAGE));
  const indexOfFirst = (currentPage - 1) * PER_PAGE;
  const currentItems = allData.slice(indexOfFirst, indexOfFirst + PER_PAGE);

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Annuler cet enregistrement ?',
      text: 'La quantité restante retourne dans le stock normal.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Oui, annuler',
      cancelButtonText: 'Non',
    });
    if (!result.isConfirmed) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${APP_API_URL}/damaged-goods/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchData();
    } catch (error) {
      Swal.fire({ icon: 'error', text: error.response?.data?.message || 'Suppression impossible.' });
    }
  };

  return (
    <MainLayout>
      <div className="duo-page">
        <div className="duo-hero duo-hero--compact mb-3">
          <p className="duo-hero-sub mb-1">Hors carrelage</p>
          <h1 className="duo-hero-title">Articles cassés / gâtés</h1>
          <p className="duo-hero-sub mt-2 mb-0">
            Chaque ligne sort du stock vendable et devient vendable au prix que vous fixez ensuite.
          </p>
        </div>
        <div className="duo-kpi-row">
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Enregistrements</p>
            <p className="duo-kpi-chip__value">{stats.total}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Quantité (filtre)</p>
            <p className="duo-kpi-chip__value">{stats.qty.toFixed(2)}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Coût de revient</p>
            <p className="duo-kpi-chip__value" style={{ fontSize: '1rem' }}>{formatNumber(stats.cost)} XOF</p>
          </div>
        </div>
        <section className="duo-panel">
          <div className="duo-toolbar">
            <span className="fw-bold">Liste</span>
            <div className="duo-toolbar__actions">
              <button type="button" className="duo-btn duo-btn--ghost duo-btn--icon" onClick={fetchData} title="Rafraîchir">
                <i className="bi bi-arrow-clockwise" />
              </button>
              <button
                type="button"
                className={`duo-btn duo-btn--ghost duo-btn--icon${filtersOpen ? ' is-open' : ''}`}
                onClick={() => setFiltersOpen((open) => !open)}
              >
                <i className={`bi ${filtersOpen ? 'bi-x-lg' : 'bi-funnel-fill'}`} />
              </button>
              <Link to="/admin/damaged-goods/sales" className="duo-btn duo-btn--ghost">Vendre</Link>
              {!isRole13 && (
                <Link to="/admin/damaged-goods/create" className="duo-btn duo-btn--primary">
                  <i className="bi bi-plus" /> Nouveau
                </Link>
              )}
            </div>
          </div>
          <div className="px-3 pt-2 pb-2 border-bottom">
            <input
              type="search"
              className="duo-input"
              placeholder="Produit, note, utilisateur…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <div className="d-flex flex-wrap gap-2 mt-2">
              {[
                { value: '', label: 'Tous' },
                { value: 'casse', label: 'Cassés' },
                { value: 'gate', label: 'Gâtés' },
              ].map((option) => (
                <button
                  key={option.label}
                  type="button"
                  className={`duo-btn duo-btn--sm${appliedFilters.reason === option.value ? ' duo-btn--primary' : ' duo-btn--ghost'}`}
                  onClick={() => {
                    setAppliedFilters((prev) => ({ ...prev, reason: option.value }));
                    setCurrentPage(1);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          {filtersOpen && (
            <form
              className="duo-filters"
              onSubmit={(e) => {
                e.preventDefault();
                setAppliedFilters((prev) => ({ ...prev, ...draftFilters }));
                setCurrentPage(1);
              }}
            >
              <div className="duo-filters__grid">
                <div>
                  <label className="duo-form-label">Date de début</label>
                  <input
                    type="date"
                    className="duo-input"
                    value={draftFilters.dateFrom}
                    onChange={(e) => setDraftFilters((prev) => ({ ...prev, dateFrom: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="duo-form-label">Date de fin</label>
                  <input
                    type="date"
                    className="duo-input"
                    value={draftFilters.dateTo}
                    onChange={(e) => setDraftFilters((prev) => ({ ...prev, dateTo: e.target.value }))}
                  />
                </div>
              </div>
              <div className="d-flex justify-content-end gap-2 mt-2">
                <button
                  type="button"
                  className="duo-btn duo-btn--ghost"
                  onClick={() => {
                    const dates = defaultFilterDates();
                    setDraftFilters(dates);
                    setSearchInput('');
                    setAppliedFilters({ search: '', reason: '', ...dates });
                    setCurrentPage(1);
                  }}
                >
                  Réinitialiser
                </button>
                <button type="submit" className="duo-btn duo-btn--primary">Appliquer</button>
              </div>
            </form>
          )}
          <div className="px-3 py-2 small text-muted">
            Plage : {appliedFilters.dateFrom} → {appliedFilters.dateTo}
          </div>
          <div className="duo-table-wrap">
            <table className="duo-table">
              <thead>
                <tr>
                  <th>Produit</th>
                  <th>Catégorie</th>
                  <th>Motif</th>
                  <th>Quantité</th>
                  <th>Coût</th>
                  <th>Date</th>
                  <th>Note</th>
                  {!isRole13 && <th />}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={8} className="duo-empty-row">Chargement…</td></tr>
                ) : currentItems.length > 0 ? currentItems.map((item) => (
                  <tr key={item.id}>
                    <td className="fw-bold">{item.variant?.name || '—'}</td>
                    <td>{item.category || '—'}</td>
                    <td>{reasonLabel(item.reason)}</td>
                    <td>{item.quantity} {item.unit || ''}</td>
                    <td>{formatNumber(item.total_cost_price)} XOF</td>
                    <td>{formatDate(item.created_at)}</td>
                    <td>{item.note || '—'}</td>
                    {!isRole13 && (
                      <td>
                        <button type="button" className="duo-btn duo-btn--ghost duo-btn--sm" onClick={() => handleDelete(item.id)}>
                          <i className="bi bi-trash" />
                        </button>
                      </td>
                    )}
                  </tr>
                )) : (
                  <tr><td colSpan={8} className="duo-empty-row">Aucun article sur cette période.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {(allData.length > 0 || isLoading) && (
            <div className="duo-footer-bar">
              <span>{isLoading ? 'Chargement…' : `${indexOfFirst + 1} – ${Math.min(indexOfFirst + PER_PAGE, allData.length)} sur ${allData.length}`}</span>
              <ul className="duo-pagination">
                <li>
                  <button type="button" className="duo-btn duo-btn--ghost" disabled={isLoading || currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>Précédent</button>
                </li>
                <li><span className="duo-pagination__info px-2">Page {currentPage} / {totalPages}</span></li>
                <li>
                  <button type="button" className="duo-btn duo-btn--primary" disabled={isLoading || currentPage >= totalPages} onClick={() => setCurrentPage((p) => p + 1)}>Suivant</button>
                </li>
              </ul>
            </div>
          )}
        </section>
      </div>
    </MainLayout>
  );
}
